import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/contexts/LanguageContext";
import { db } from "@/lib/firebase";
import { collection, query, where, getDocs } from "firebase/firestore";
import { formatCurrency } from "@/config/company.config";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { 
  Handshake, Search, Plus, Building, Calendar, DollarSign, 
  MessageSquare, Link as LinkIcon, Users, Video 
} from "lucide-react";

interface Partnership {
  id: string;
  partnerId: string;
  partnerName: string;
  partnerType: 'buyer' | 'seller' | 'broker' | 'lender' | 'consultant';
  partnerCompany?: string;
  specialty?: string;
  status: 'pending' | 'active' | 'inactive';
  startDate: any;
  sharedDeals: string[];
  revenue: number;
  meetings: number;
  calendarMerged: boolean;
  mergedCalendarUrl?: string;
}

const mockPartnerships: Partnership[] = [
  {
    id: "mock-partnership-1",
    partnerId: "partner-1",
    partnerName: "Cabinet Lavoie & Fils",
    partnerType: "broker",
    partnerCompany: "Lavoie M&A Services",
    specialty: "Fusions & Acquisitions",
    status: "active",
    startDate: new Date(Date.now() - 180 * 24 * 60 * 60 * 1000),
    sharedDeals: ["deal-123", "deal-456"],
    revenue: 35000,
    meetings: 12,
    calendarMerged: true,
    mergedCalendarUrl: "/calendar/merged/lavoie",
  },
  {
    id: "mock-partnership-2",
    partnerId: "partner-2",
    partnerName: "Groupe Financier Montréal",
    partnerType: "lender",
    partnerCompany: "GFM Capital",
    specialty: "Financement Acquisitions",
    status: "active",
    startDate: new Date(Date.now() - 90 * 24 * 60 * 60 * 1000),
    sharedDeals: ["deal-789"],
    revenue: 18500,
    meetings: 6,
    calendarMerged: false,
  },
  {
    id: "mock-partnership-3",
    partnerId: "partner-3",
    partnerName: "Notaires Leblanc",
    partnerType: "consultant",
    partnerCompany: "Leblanc Notariat",
    specialty: "Transactions Immobilières",
    status: "pending",
    startDate: new Date(),
    sharedDeals: [],
    revenue: 0,
    meetings: 2,
    calendarMerged: false,
  },
  {
    id: "mock-partnership-4",
    partnerId: "partner-4",
    partnerName: "Marie Gagnon",
    partnerType: "seller",
    partnerCompany: "Gagnon Industries",
    specialty: "Manufacturing",
    status: "active",
    startDate: new Date(Date.now() - 45 * 24 * 60 * 60 * 1000),
    sharedDeals: ["deal-101"],
    revenue: 12000,
    meetings: 8,
    calendarMerged: true,
  },
];

const translations = {
  en: {
    title: "Partnerships",
    subtitle: "Manage your business relationships and collaborations",
    addPartner: "Add Partner",
    searchPartners: "Search partners...",
    allStatus: "All Status",
    pending: "Pending",
    active: "Active",
    inactive: "Inactive",
    noPartnerships: "No partnerships yet",
    partnershipsHint: "You'll be added as a partner when clients select you for their deals",
    buyer: "Buyer",
    seller: "Seller",
    broker: "Broker",
    lender: "Lender",
    consultant: "Consultant",
    specialty: "Specialty",
    sharedDeals: "Shared Deals",
    revenue: "Revenue",
    meetings: "Meetings",
    calendarIntegration: "Calendar Integration",
    merged: "Merged",
    notMerged: "Not Merged",
    viewCalendar: "View Calendar",
    mergeCalendar: "Merge Calendar",
    message: "Message",
    scheduleMeeting: "Schedule Meeting",
    loading: "Loading partnerships...",
    since: "Since",
  },
  fr: {
    title: "Partenariats",
    subtitle: "Gérez vos relations et collaborations commerciales",
    addPartner: "Ajouter Partenaire",
    searchPartners: "Rechercher partenaires...",
    allStatus: "Tous Statuts",
    pending: "En Attente",
    active: "Actif",
    inactive: "Inactif",
    noPartnerships: "Aucun partenariat pour le moment",
    partnershipsHint: "Vous serez ajouté comme partenaire lorsque les clients vous sélectionneront pour leurs deals",
    buyer: "Acheteur",
    seller: "Vendeur",
    broker: "Courtier",
    lender: "Prêteur",
    consultant: "Consultant",
    specialty: "Spécialité",
    sharedDeals: "Deals Partagés",
    revenue: "Revenus",
    meetings: "Réunions",
    calendarIntegration: "Intégration Calendrier",
    merged: "Fusionné",
    notMerged: "Non Fusionné",
    viewCalendar: "Voir Calendrier",
    mergeCalendar: "Fusionner Calendrier",
    message: "Message",
    scheduleMeeting: "Planifier Réunion",
    loading: "Chargement des partenariats...",
    since: "Depuis",
  },
};

export default function ConsultantPartnerships() {
  const { user } = useAuth();
  const { language } = useLanguage();
  const { toast } = useToast();
  const t = translations[language as keyof typeof translations] || translations.en;

  const [partnerships, setPartnerships] = useState<Partnership[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState<string>("all");

  useEffect(() => {
    if (user) {
      loadPartnerships();
    }
  }, [user]);

  const loadPartnerships = async () => {
    if (!user) return;
    setIsLoading(true);
    try {
      const partnershipsRef = collection(db, "consultantPartnerships");
      const partnershipsQuery = query(
        partnershipsRef,
        where("consultantId", "==", user.email)
      );
      const partnershipsSnapshot = await getDocs(partnershipsQuery);
      const partnershipsData = partnershipsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Partnership[];
      setPartnerships(partnershipsData.length > 0 ? partnershipsData : mockPartnerships);
    } catch (error) {
      console.error("Error loading partnerships:", error);
      setPartnerships(mockPartnerships);
    } finally {
      setIsLoading(false);
    }
  };

  const filteredPartnerships = partnerships.filter(partnership => {
    const matchesSearch = partnership.partnerName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         partnership.partnerCompany?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         partnership.specialty?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = filterStatus === "all" || partnership.status === filterStatus;
    return matchesSearch && matchesFilter;
  });

  const getTypeLabel = (type: string) => {
    return t[type as keyof typeof t] || type;
  };

  const getTypeBadgeVariant = (type: string): "default" | "secondary" | "outline" => {
    switch (type) {
      case 'broker': return 'default';
      case 'lender': return 'secondary';
      case 'consultant': return 'outline';
      default: return 'outline';
    }
  };

  const getStatusBadgeVariant = (status: string): "default" | "secondary" | "outline" => {
    switch (status) {
      case 'active': return 'default';
      case 'pending': return 'secondary';
      case 'inactive': return 'outline';
      default: return 'outline';
    }
  };

  // formatCurrency is now imported from company.config

  const formatDate = (date: any) => {
    const d = date instanceof Date ? date : date?.toDate?.() || new Date(date);
    return new Intl.DateTimeFormat(language === 'fr' ? 'fr-CA' : 'en-CA', {
      month: 'short',
      year: 'numeric'
    }).format(d);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">{t.title}</h1>
          <p className="text-muted-foreground mt-2">{t.subtitle}</p>
        </div>
        <Button>
          <Plus className="mr-2 h-4 w-4" />
          {t.addPartner}
        </Button>
      </div>

      {/* Search & Filter */}
      <div className="flex flex-col md:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder={t.searchPartners}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-full md:w-[180px]">
            <SelectValue placeholder={t.allStatus} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t.allStatus}</SelectItem>
            <SelectItem value="active">{t.active}</SelectItem>
            <SelectItem value="pending">{t.pending}</SelectItem>
            <SelectItem value="inactive">{t.inactive}</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Partnerships Grid */}
      {filteredPartnerships.length > 0 ? (
        <div className="grid gap-4 md:grid-cols-2">
          {filteredPartnerships.map((partnership) => (
            <Card key={partnership.id} className="hover:shadow-md transition-shadow">
              <CardHeader className="pb-3">
                <div className="flex items-start gap-4">
                  <Avatar className="h-12 w-12">
                    <AvatarFallback className="bg-primary/10 text-primary">
                      {partnership.partnerName.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <Badge variant={getStatusBadgeVariant(partnership.status)}>
                        {t[partnership.status as keyof typeof t] || partnership.status}
                      </Badge>
                      <Badge variant={getTypeBadgeVariant(partnership.partnerType)}>
                        {getTypeLabel(partnership.partnerType)}
                      </Badge>
                    </div>
                    <CardTitle className="text-lg truncate">{partnership.partnerName}</CardTitle>
                    <CardDescription className="flex items-center gap-1 mt-1">
                      <Building className="h-3 w-3" />
                      <span className="truncate">{partnership.partnerCompany || 'Independent'}</span>
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {partnership.specialty && (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Handshake className="h-4 w-4" />
                    <span>{t.specialty}: {partnership.specialty}</span>
                  </div>
                )}

                <div className="text-sm text-muted-foreground">
                  {t.since} {formatDate(partnership.startDate)}
                </div>

                <div className="grid grid-cols-3 gap-4 pt-3 border-t">
                  <div className="text-center">
                    <div className="flex items-center justify-center gap-1 text-muted-foreground">
                      <LinkIcon className="h-4 w-4" />
                    </div>
                    <p className="text-lg font-semibold">{partnership.sharedDeals.length}</p>
                    <p className="text-xs text-muted-foreground">{t.sharedDeals}</p>
                  </div>
                  <div className="text-center">
                    <div className="flex items-center justify-center gap-1 text-muted-foreground">
                      <DollarSign className="h-4 w-4" />
                    </div>
                    <p className="text-lg font-semibold">{formatCurrency(partnership.revenue)}</p>
                    <p className="text-xs text-muted-foreground">{t.revenue}</p>
                  </div>
                  <div className="text-center">
                    <div className="flex items-center justify-center gap-1 text-muted-foreground">
                      <Video className="h-4 w-4" />
                    </div>
                    <p className="text-lg font-semibold">{partnership.meetings}</p>
                    <p className="text-xs text-muted-foreground">{t.meetings}</p>
                  </div>
                </div>

                <div className="flex items-center justify-between pt-2 border-t">
                  <div className="flex items-center gap-2 text-sm">
                    <Calendar className="h-4 w-4 text-muted-foreground" />
                    <span className="text-muted-foreground">{t.calendarIntegration}:</span>
                    <Badge variant={partnership.calendarMerged ? "default" : "outline"}>
                      {partnership.calendarMerged ? t.merged : t.notMerged}
                    </Badge>
                  </div>
                </div>

                <div className="flex gap-2 pt-2">
                  <Button variant="outline" size="sm" className="flex-1">
                    <MessageSquare className="h-4 w-4 mr-1" />
                    {t.message}
                  </Button>
                  <Button variant="outline" size="sm" className="flex-1">
                    <Video className="h-4 w-4 mr-1" />
                    {t.scheduleMeeting}
                  </Button>
                  {partnership.calendarMerged ? (
                    <Button size="sm" className="flex-1">
                      <Calendar className="h-4 w-4 mr-1" />
                      {t.viewCalendar}
                    </Button>
                  ) : (
                    <Button size="sm" className="flex-1">
                      <LinkIcon className="h-4 w-4 mr-1" />
                      {t.mergeCalendar}
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Handshake className="h-12 w-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold">{t.noPartnerships}</h3>
            <p className="text-muted-foreground text-center mt-2 mb-4 max-w-md">
              {t.partnershipsHint}
            </p>
            <Button>
              <Plus className="mr-2 h-4 w-4" />
              {t.addPartner}
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
